import { TerminalsModel } from '../models/terminals-model'
import { VoyagePlan } from '../models/vessel-planning-model'
import { VoyageImoModel } from '../models/voyage-imo-model'
import * as Converters from '../../src/converters/converters'
import { VoyageDialogPopupManager } from '../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-manager'
import { PlanSource } from '../models/voyage-plans-model'
import { VoyageRoute, VoyageRouteLineFeature, VoyageRoutePointFeature } from '../models/voyage-routing-model'
import { WeatherData } from '../../local_modules/vessel-performance/src/vessel-performance-main'
import { SpotWeather } from './route-weather-worker'
import {
    TimeseriesData,
    RouteSeriesNames,
    WeatherSeries,
} from '../../local_modules/voy-timeseries-chart/src/echartUtils'
import { Format } from '../converters/types/converters.types'
import { mpsToKnots } from './helpers'
import { VesselPositionData } from '../services/types/position-service.types'
import { SafetyChecks, UnReadWRAdvice } from "../models/weather-routing-model-impl";
import { html } from "lit";
import { captureStarRouteWrWorkflowEvent } from "./rum-utility";

export function saveFile(name: string, mime: string, str: string) {
    var blob = new Blob([str], { type: mime })

    console.log(`%c saving: ${name}`)

    const exportElement = document.createElement('a')
    exportElement.download = name
    exportElement.href = URL.createObjectURL(blob)
    exportElement.click()
}

export function getDepartureArrivalCityFileName(plan: VoyagePlan): string {
    // Assume at-sea if we don't have a terminal code
    let departureName = 'At-Sea'
    let arrivalName = 'At-Sea'

    if (plan.departureTerminalCode) {
        departureName =
            TerminalsModel!.getCity(plan.departureTerminalCode) ||
            plan.departureTerminalCode
    }

    if (plan.arrivalTerminalCode) {
        arrivalName =
            TerminalsModel!.getCity(plan.arrivalTerminalCode) ||
            plan.departureTerminalCode
    }

    return `${departureName}-${arrivalName}`
}

export function checkIfPilotEtdIsLessThanCurrentTime(pilotEtd: number): boolean {
    const currentTime = new Date().getTime();
    const pilotEtdTime = new Date(pilotEtd).getTime();
    return pilotEtdTime < currentTime;
}

// Route provenance for import/export telemetry. See docs/route-import-export-telemetry.md.
export type RouteOrigin = 'star-route' | 'star-route-edited-in-app' | 'weather-routing' | 'own-route';
export type RouteUploadSource = 'star-route-round-trip' | 'own-route';

export function getRouteOrigin(route: VoyageRoute): RouteOrigin {
    if (route.routeCreatedBy === "system") {
        // Editing a Star Route in the app creates a new 'system' route whose routeIdParent is the Star Route's BPR id
        return route.routeIdParent ? 'star-route-edited-in-app' : 'star-route';
    }
    return route.routeCreatedBy === "WR" ? 'weather-routing' : 'own-route';
}

// Exported files are named `<departure>-<arrival>-<creationTime>` with ':' replaced by '_' (the browser does that anyway).
export function getRouteExportFileName(plan: VoyagePlan, route: VoyageRoute): string {
    return `${getDepartureArrivalCityFileName(plan)}-${route.creationTime}`.replaceAll(':', '_')
}

// Provenance only, never similarity: an upload counts as a Star Route round trip when its file name still carries the
// creationTime of a Star Route in the plan, i.e. the file came from exporting that Star Route. A route that merely looks
// like a Star Route (renamed file, hand-drawn copy, regenerated Star Routes) is classified as an own route.
export function findExportedStarRouteForImport(routes: VoyageRoute[] | undefined, importedFileName: string): VoyageRoute | undefined {
    const normalizedFileName = importedFileName.replaceAll(':', '_');
    return routes?.find(route =>
        route.routeCreatedBy === "system" &&
        !!route.creationTime &&
        normalizedFileName.includes(route.creationTime.replaceAll(':', '_')),
    );
}

// Exports round coordinates differently per format (e.g. 0.1' in Nacos/JRC), so waypoints within this distance are equal
const REIMPORT_WAYPOINT_TOLERANCE_NM = 0.2;

function getPointCoordinates(
    geometry: Array<VoyageRouteLineFeature | VoyageRoutePointFeature> | undefined,
    isIncluded: (type: string | undefined) => boolean,
): [number, number][] {
    return (geometry ?? [])
        .filter((feature): feature is VoyageRoutePointFeature => feature.geometry?.type === "Point" && isIncluded(feature.properties?.type))
        .map(feature => feature.geometry.coordinates);
}

function isSameWaypoint([lon1, lat1]: [number, number], [lon2, lat2]: [number, number]): boolean {
    const deltaLonNm = (lon1 - lon2) * 60 * Math.cos(((lat1 + lat2) / 2) * Math.PI / 180);
    const deltaLatNm = (lat1 - lat2) * 60;
    return Math.hypot(deltaLonNm, deltaLatNm) <= REIMPORT_WAYPOINT_TOLERANCE_NM;
}

// Compares the waypoints of a re-uploaded Star Route file with the Star Route it was exported from. Most formats export
// only the 'Hard' waypoints, RTZ also exports the computed 'Weather' points (which come back as waypoints), so uploaded
// points sitting on one of the Star Route's weather points are ignored. Unmodified means the remaining waypoints are a
// contiguous run of the Star Route's waypoints, so pilot-to-pilot exports that drop the berth legs still count as unmodified.
export function compareReimportedRouteWithStarRoute(
    importedGeometry: Array<VoyageRouteLineFeature | VoyageRoutePointFeature> | undefined,
    starRouteGeometry: Array<VoyageRouteLineFeature | VoyageRoutePointFeature> | undefined,
): { wasModified: boolean; waypointCountDelta: number } {
    const starWaypoints = getPointCoordinates(starRouteGeometry, type => type === "Hard");
    const starWeatherPoints = getPointCoordinates(starRouteGeometry, type => type === "Weather");
    const imported = getPointCoordinates(importedGeometry, type => type !== "Weather")
        .filter(waypoint => !starWeatherPoints.some(weatherPoint => isSameWaypoint(waypoint, weatherPoint)));
    const waypointCountDelta = imported.length - starWaypoints.length;

    if (imported.length === 0 || imported.length > starWaypoints.length) {
        return { wasModified: true, waypointCountDelta };
    }

    for (let offset = 0; offset <= starWaypoints.length - imported.length; offset++) {
        if (imported.every((waypoint, i) => isSameWaypoint(waypoint, starWaypoints[offset + i]))) {
            return { wasModified: false, waypointCountDelta };
        }
    }
    return { wasModified: true, waypointCountDelta };
}

function captureRouteExport(plan: VoyagePlan, route: VoyageRoute, format: Format, fileName: string) {
    captureStarRouteWrWorkflowEvent("route-exported", {
        scheduleId: plan.scheduleId,
        planId: plan.id ?? null,
        routeId: route.routeId,
        routeName: route.name,
        format,
        fileName,
        routeOrigin: getRouteOrigin(route),
        routeCreatedBy: route.routeCreatedBy,
        isStarRoute: route.routeCreatedBy === "system",
        routeIdParent: route.routeIdParent,
        bestPracticeRouteId: route.bestPracticeRouteId,
        routeCreationTime: route.creationTime,
        uiType: route.uiType,
        currentSelectedRouteId: plan.currentSelectedRouteId,
        isApprovedPlan: plan.status === "approved",
        wrRequired: route.passagePlan?.weatherRouting?.required ?? null,
        wrAcknowledged: route.passagePlan?.weatherRouting?.acknowledged ?? null,
    });
}

export function downloadPlanWithSelectedRoute(plan: VoyagePlan, format: Format)
{
    const routeIndex = plan.routes.findIndex(route => route.routeId === plan.currentSelectedRouteId) ?? plan.selectedRouteIndex
    const route = plan.routes[routeIndex];
    if (!route) return;

    try
    {
        const filename = getRouteExportFileName(plan, route)
        const exported = Converters.exportPlan(plan, format, filename)
        if (exported.data) {
            captureRouteExport(plan, route, format, `${filename}.${exported.fileExt}`);
            saveFile(
                `${filename}.${exported.fileExt}`,
                exported.mimeType,
                exported.data,
            )
        }
    } catch (err) {
        VoyageDialogPopupManager.createDialog({
            headerText: 'Exporting route failed',
            bodyText: 'Error occurred when attempting to export route',
            isError: true,
            errorDetails: err instanceof Error ? err.message : <string>err,
        })
    }
}

function trimArray(array: any, fromIndex: number, toIndex: number) {
  return [
    ...array.slice(fromIndex, ++toIndex),
  ];
}

export async function loadExcelJS(): Promise<any> {
  if ((window as any).ExcelJS) {
    return (window as any).ExcelJS;
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js";
    script.async = true;
    script.onload = () => resolve((window as any).ExcelJS);
    script.onerror = () => reject(new Error("Failed to load ExcelJS from CDN"));
    document.head.appendChild(script);
  });
}

export async function downloadBasicPlanWithSelectedRoute({
    plan = {} as VoyagePlan,
    format = '' as Format, 
    editRouteFn,
}: {
    plan?: VoyagePlan,
    format?: Format,
    editRouteFn?: (routeId: string) => Promise<void>,
}) {
    console.log('Exporting basic plan with selected route', plan)
    const routeIndex =
        plan.routes.findIndex(
            (route:any) => route.routeId === plan.currentSelectedRouteId,
        ) ?? plan.selectedRouteIndex
    const route = plan.routes[routeIndex]
    if (!route) return
    const ExcelJS = await loadExcelJS();
    // Same naming as the other formats so a re-uploaded Excel export can be traced back to its route
    const fileName = `${getRouteExportFileName(plan, route)}.xlsx`;
    captureRouteExport(plan, route, format, fileName);
    executeDownLoadExcel({ plan, format, editRouteFn, ExcelJS, route, fileName });
}

async function executeDownLoadExcel({
    plan = {} as VoyagePlan,
    format = '' as Format, 
    editRouteFn,
    ExcelJS,
    route,
    fileName,
}: {
    plan?: VoyagePlan,
    format?: Format,
    editRouteFn?: (routeId: string) => Promise<void>,
    ExcelJS: any,
    route: any,
    fileName: string,
}) {
    if (!ExcelJS) {
        throw new Error('ExcelJS library is not available')
    } else {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('WayPoint Listing Template');

        const orangeLightFill: any = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'f8e5d8' },
        }
        const orangeLightFill2: any = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'f9e6d9' },
        }
        const blueLightFill: any = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'e0eaf6' },
        }
        const greenFill: any = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'd9e4c1' },
        }
        const yellowFill: any = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'ffff54' },
        }
        const orangeFill: any = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'e98866' },
        }
        const greyFill: any = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'd9d9d9' },
        }

        // Row 1
        worksheet.getCell('A1').value = 'Version no:'
    worksheet.getCell('B1').value = 2.2
        ;['A1', 'B1'].forEach((cellRef) => {
            const cell = worksheet.getCell(cellRef)
            cell.alignment = { vertical: 'middle', horizontal: 'center' }
            cell.fill = greyFill
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' },
            }
        })

        // Row 2 – Title
        worksheet.getCell('A2').value = ''
        worksheet.mergeCells('B2:V2')
        worksheet.getCell('B2').value = 'WayPoint Listing Template'
        worksheet.getCell('B2').alignment = {
            vertical: 'middle',
            horizontal: 'left',
        }
        worksheet.getCell('B2').font = { bold: true, size: 14 }

        // Row 3
        worksheet.mergeCells('B3:Y3')
        worksheet.getCell('B3').value =
            'Please enter the pilot to pilot coordinates in the below rows only'
        worksheet.getCell('B3').fill = orangeFill
        worksheet.getCell('B3').alignment = { horizontal: 'center' }
        worksheet.getCell('B3').font = { size: 10, color: { argb: 'ffffff' } }
        worksheet.getCell('B3').border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
        }

        // Row 4 - 7
        ;['A4', 'A5', 'A6', 'A7'].forEach((cellRef) => {
            const cell = worksheet.getCell(cellRef)
            cell.value = ''
        })
        worksheet.mergeCells('B4:K7')
        worksheet.mergeCells('L4:U7')
        worksheet.getCell('B4').value = 'Latitude'
        worksheet.getCell('L4').value = 'Longitude'
        worksheet.getCell('B4').fill = orangeLightFill
        worksheet.getCell('L4').fill = blueLightFill
        ;['L4', 'B4'].forEach((cellRef) => {
            const cell = worksheet.getCell(cellRef)
            cell.alignment = { vertical: 'middle', horizontal: 'center' }
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' },
            }
        })
        worksheet.mergeCells('V4:V7')
        worksheet.mergeCells('W4:W7')
        worksheet.mergeCells('X4:X7')
        worksheet.mergeCells('Y4:Y7')
        worksheet.getCell('V4').value =
            'Notice that RL/GC is departing from waypoint placed in the same line'
        worksheet.getCell('W4').value = 'For a leg, HRA ect. Only if required'
        worksheet.getCell('X4').value =
            'Considering water depth, traffic ect. Only if required'
        worksheet.getCell('Y4').value = 'Found on the leg'
        ;['V4', 'W4', 'X4', 'Y4'].forEach((cellRef) => {
            const cell = worksheet.getCell(cellRef)
            cell.alignment = {
                vertical: 'bottom',
                horizontal: 'center',
                wrapText: true,
            }
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' },
            }
        })

        // Row 8
        const row8 = worksheet.getRow(8)
        row8.values = [
            '',
            'Degrees',
            '',
            '',
            'Minutes',
            '',
            '',
            '',
            '',
            'Hemisphere',
            '',
            'Degrees',
            '',
            '',
            'Minutes',
            '',
            '',
            '',
            '',
            'Hemisphere',
            '',
            'leg',
            'Speed Min Req.',
            'Speed Max',
            'Min Depth',
            '',
        ]
        row8.eachCell((cell: any, i: number) => {
            if (i > 1 && i < 12) {
                cell.fill = orangeLightFill
            } else if (i >= 12 && i < 22) {
                cell.fill = blueLightFill
            }
            if (i > 1 && i < 26) {
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' },
                }
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: 'center',
                    wrapText: true,
                }
            }
        })

        // Row 9
        const row9 = worksheet.getRow(9)
        row9.values = [
            '',
            '(0-90)',
            'DEGREES CORRECTED',
            '',
            '(0.00-59.99)',
            'MINUTES',
            '',
            'SECONDS',
            '',
            'N OR S',
            '',
            '(90-180)',
            'DEGREES CORRECTED',
            '',
            '(0.00-59.99)',
            'MINUTES',
            '',
            'SECONDS',
            '',
            'E OR W',
            '',
            '(RL/GC)',
            'Kn',
            'Kn',
            'm',
            '',
            'Output Column',
        ]
        worksheet.getCell('Z9').fill = greyFill
        worksheet.getCell('AA9').fill = greyFill
        worksheet.getCell('AA9').alignment = {
            wrapText: true,
            vertical: 'middle',
            horizontal: 'left',
        }
        row9.eachCell((cell:any, i:number) => {
            if (i > 1 && i < 12) {
                cell.fill = orangeLightFill
            } else if (i >= 12 && i < 22) {
                cell.fill = blueLightFill
            } else if (i >= 22 && i < 26) {
                cell.fill = greenFill
            }
            if (i > 1 && i < 28) {
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' },
                }
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: 'center',
                    wrapText: true,
                }
            }
        })

        const selectedRouteGeometry = route.route_geometry
        let wayPointsDetails: (string | number)[][] = [];
        const pilotPointsArray = [];
        let firstPilotPointIndex = 0;
        let lastPilotPointIndex = 0;
        let waypointIndex = 1;

        selectedRouteGeometry.forEach((geometry:any, i:number) => {
            if (geometry.geometry.type === 'Point' && geometry.properties.pilotPoint) {
                pilotPointsArray.push(i);
                if(firstPilotPointIndex === 0) {
                    firstPilotPointIndex = i;
                }
                if(i > firstPilotPointIndex) {
                    lastPilotPointIndex = i;
                }
            }
        });
        // to make sure minimum 2 pilot points are present
        if (pilotPointsArray.length < 2) {
            const currentSelectedRoute: any = plan.routes.find(r => r.routeId === plan?.currentSelectedRouteId);
            const errorPopup = await VoyageDialogPopupManager.createDialog({
                headerText: "The selected Route contains fewer than 2 pilot waypoints.",
                bodyText: "All plans require at least 2 waypoints marked as 'Pilot' waypoints to correctly calculate a speed profile.\nIf your plan starts or ends in an area where no pilot is required, \n set the first or last waypoint as a pilot waypoint (e.g., arriving or departing from an anchorage)",
                acceptButtonText: "Edit Route",
                rejectButtonText: "Cancel"
            });

            const res = await errorPopup.getResponse();
            if (res === "accept" && editRouteFn) {
                await editRouteFn(currentSelectedRoute?.routeId);
            } 

            return;
        }
        const trimmedRouteGeometry = (firstPilotPointIndex > 0 && lastPilotPointIndex > 0) ? trimArray(selectedRouteGeometry, firstPilotPointIndex, (lastPilotPointIndex+1)) : selectedRouteGeometry;

        const MpsToKts = 60 * 60 / 1852;

        trimmedRouteGeometry.forEach((geometry:any, i:number) => {
            if(geometry.geometry.type === 'Point') {
                const point = geometry.geometry.coordinates
                const lat = point[1]
                const lon = point[0]
                const latDMM = toDMM(lat, true)
                const lonDMM = toDMM(lon, false)

                const formattedLat = toDMS(lat, true)
                const formattedLon = toDMS(lon, false)

                const minSpeedVal = Number(geometry.properties?.speed_minimum) * MpsToKts;
                const minSpeed =
                    (geometry.properties?.speed_minimum === null ||
                    geometry.properties?.speed_minimum === undefined ||
                    geometry.properties?.speed_minimum === '')
                        ? ''
                        : minSpeedVal.toFixed(1);

                const maxSpeedVal = Number(geometry.properties?.speed_maximum) * MpsToKts;
                const maxSpeed =
                    (geometry.properties?.speed_maximum === null ||
                    geometry.properties?.speed_maximum === undefined ||
                    geometry.properties?.speed_maximum === '')
                        ? ''
                        : maxSpeedVal.toFixed(1);

                const minDepthVal = geometry?.properties?.userInput?.min_depth || geometry?.properties?.min_depth
                const min_depth =
                    (minDepthVal === 0 ||
                    minDepthVal === null ||
                    minDepthVal === undefined ||
                    minDepthVal === '')
                        ? ''
                        : minDepthVal.toFixed(1);
                wayPointsDetails.push([
                    '',
                    latDMM.degrees,
                    latDMM.degrees,
                    '°',
                    latDMM.minutes,
                    latDMM.minutes,
                    "'",
                    latDMM.seconds,
                    '"',
                    latDMM.hemisphere,
                    ',',
                    lonDMM.degrees,
                    lonDMM.degrees,
                    '°',
                    lonDMM.minutes,
                    lonDMM.minutes,
                    "'",
                    latDMM.seconds,
                    '"',
                    lonDMM.hemisphere,
                    ',',
                    'GC/RL',
                    minSpeed,
                    maxSpeed,
                    min_depth,
                    `Waypoint ${waypointIndex}`,
                    `${formattedLat}, ${formattedLon} ,Waypoint ${waypointIndex}`,
                ])
                waypointIndex = waypointIndex+1;
            } else if (geometry.geometry.type === 'LineString') {
                const leg = geometry.properties.leg_type === "GreatCircle"
                    ? 'GC'
                    : geometry.properties.leg_type === "Rhumbline" ? 'RL': '';    
                if(i >= 1 && i<= trimmedRouteGeometry.length) {
                    const ind = (i-1)/2;
                    if(Array.isArray(wayPointsDetails[ind]) && wayPointsDetails[ind].length > 0){
                        // Find the index of the item to replace
                        const index = wayPointsDetails[ind].findIndex(item => item === 'GC/RL'); 
                        wayPointsDetails[ind][index] = leg;  
                    }  
                } 
            }
        });

        wayPointsDetails.forEach((data, i) => {
            const row = worksheet.addRow(data)
            row.eachCell((cell:any, i:number) => {
                if (i > 1 && i < 12) {
                    cell.fill = orangeLightFill
                } else if (i >= 12 && i < 22) {
                    cell.fill = blueLightFill
                } else if (i >= 22 && i < 26) {
                    cell.fill = greenFill
                }
                if (i > 1 && i < 28) {
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' },
                    }
                }
            })
        })

        //Metadata on right side
        const departurePilotEtd = new Date(plan.departurePilotEtd)
        const arrivalPilotEta = new Date(plan.arrivalPilotEta)

        const meta = [
            ['IMO', VoyageImoModel!.imo, '', 'VOY no.', '------'],
            [
                'Departure Terminal (RKST)',
                plan.departureTerminalCode,
                '',
                'GM (m)',
                plan.gmt,
            ],
            [
                'Arrival Terminal (RKST)',
                plan.arrivalTerminalCode,
                '',
                'Live reefers',
                plan.numberOfReefers,
            ],
            [
                'Forward Draft (m)',
                plan.foreDraft,
                '',
                '',
                'Day',
                'Month',
                '',
                '',
                '',
                'Year',
                'Hour',
                'Minutes',
            ],
            [
                'Aft Draft (m)',
                plan.aftDraft,
                '',
                'ETD Pilot (UTC)',
                departurePilotEtd.getUTCDate(),
                departurePilotEtd.getUTCMonth() + 1,
                '',
                '',
                '',
                departurePilotEtd.getUTCFullYear(),
                departurePilotEtd.getUTCHours(),
                departurePilotEtd.getUTCMinutes(),
            ],
            [
                'Pilot to Pilot Distance (NM)',
                '--',
                '',
                'ETA Pilot (UTC)',
                arrivalPilotEta.getUTCDate(),
                arrivalPilotEta.getUTCMonth() + 1,
                '',
                '',
                '',
                arrivalPilotEta.getUTCFullYear(),
                arrivalPilotEta.getUTCHours(),
                arrivalPilotEta.getUTCMinutes(),
            ],
            ['Planned Speed for voyage (Kn)', '--'],
            ['Planned HRA Speed (kn/NA)', '---'],
        ]

        const metaStartRow = 1
        const metaStartCol = 27

        meta.forEach((rowData, rowIndex) => {
            const row = worksheet.getRow(metaStartRow + rowIndex)

            rowData.forEach((value: any, colIndex: number) => {
                const cell = row.getCell(metaStartCol + colIndex)

                cell.value = value

                if (colIndex === 0 || colIndex === 3) {
                    cell.font = { bold: true }
                }

                const yellowFillRows =
                    (colIndex === 1 ||
                        colIndex === 4 ||
                        colIndex === 5 ||
                        colIndex === 6 ||
                        colIndex === 7 ||
                        colIndex === 8 ||
                        colIndex === 9 ||
                        colIndex === 10 ||
                        colIndex === 11) &&
                    !(rowIndex === 3)

                if (typeof value !== 'undefined' && yellowFillRows) {
                    cell.fill = yellowFill
                }
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' },
                }
                cell.alignment = { vertical: 'middle', horizontal: 'center' }
            })
        })

        worksheet.columns = [
            { width: 10 },
            { width: 10 },
            { width: 10 },
            { width: 5 },
            { width: 10 },
            { width: 10 },
            { width: 5 },
            { width: 10 },
            { width: 5 },
            { width: 10 },
            { width: 5 },
            { width: 10 },
            { width: 10 },
            { width: 5 },
            { width: 10 },
            { width: 10 },
            { width: 5 },
            { width: 10 },
            { width: 5 },
            { width: 10 },
            { width: 5 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 20 },
            { width: 10 },
            { width: 30 },
            { width: 20 },
            { width: 2 },
            { width: 25 },
            { width: 15 },
            { width: 8 },
            { width: 8 },
            { width: 8 },
            { width: 8 },
        ]

        const buffer = await workbook.xlsx.writeBuffer();
 
        const excelData = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        
        const url = URL.createObjectURL(excelData);
        const hiddenElement = document.createElement('a');
        hiddenElement.href = url;
        hiddenElement.download = fileName;
        hiddenElement.click();
    }
}

export function getFormattedDataSource(dataSource: string) {
    const srcMap = new Map([
        ['spire', 'Spire'],
        ['mt', 'MarineTraffic'],
        ['fbb', 'FBB'],
        ['inmarsat', 'Inmarsat'],
        ['sc', 'StarConnect'],
        ['default', '--'],
    ])
    return srcMap.get(dataSource) ?? '--'
}

export function getAisStatusLabel(aisStatus?: number | null): string {
    if (aisStatus === undefined || aisStatus === null) {
        return '--'
    }

    const NavStatusMap = new Map([
        [0, 'Under way using engine'],
        [1, 'At anchor'],
        [2, 'Not under command'],
        [3, 'Restricted maneuverability'],
        [4, 'Constrained by her draught'],
        [5, 'Moored'],
        [6, 'Aground'],
        [7, 'Engaged in fishing'],
        [8, 'Underway sailing'],
    ])
    return NavStatusMap.get(aisStatus) ?? '--'
}

export function speedTooltipText(plan: VoyagePlan) {
    switch (plan?.source) {
        case PlanSource.PASSAGE_PLAN:
            return 'Planned speed for this vessel is manually entered by bridge crew for entire voyage between pilot points'
        case PlanSource.ECOVOYAGE:
        case PlanSource.VOYAGE_PLANNING:
            return 'Planned speed for this vessel is calculated per leg based on vessel type, weather and vessels power setting'
        default:
            return 'Plan and planned speed for this vessel is currently not available'
    }
}

export function isPromise(p: Promise<any>, context: any) {
    if (typeof p === 'object' && typeof p.then === 'function') {
        return true
    }

    return false
}

export enum UserOS {
    WINDOWS = 'windows',
    MAC = 'mac',
    LINUX = 'linux',
    UNIX = 'unix',
    UNKNOWN = 'unknown',
}

export function getOS() {
    let OSName = UserOS.UNKNOWN
    let navApp = navigator.userAgent.toLowerCase()
    switch (true) {
        case navApp.indexOf('win') != -1:
            OSName = UserOS.WINDOWS
            break
        case navApp.indexOf('mac') != -1:
            OSName = UserOS.MAC
            break
        case navApp.indexOf('linux') != -1:
            OSName = UserOS.LINUX
            break
        case navApp.indexOf('x11') != -1:
            OSName = UserOS.UNIX
            break
    }
    return OSName
}

export function createTimeseriesTemplate(
    name: string,
    color: string,
): TimeseriesData {
    return {
        name: name,
        color: color,
        timeseries: {
            values: [],
            timestamps: [],
            direction: [],
        },
    }
}

export function extractSpotWeatherDataFromRoute(
    routes: VoyageRoute[],
    mode: string,
): WeatherData {
    const updatedWeatherData: WeatherData = {
        wind50M: [],
        currents: [],
        wave: [],
        swell: [],
    }

    // all routes
    for (let i = 0; i < routes.length; i++) {
        let color = ''
        const routeIndex = i + 1
        if (mode === 'planning') {
            color = `--route${routeIndex}-color`
        } else {
            color = '--performance-viewer-actual-color'
        }

        let name = 'forecasted'
        if (mode === 'planning') {
            name = <RouteSeriesNames>`Route ${routeIndex}`
        }

        const weatherSeries: WeatherSeries = {
            wind50M: createTimeseriesTemplate(name, color),
            swell: createTimeseriesTemplate(name, color),
            wave: createTimeseriesTemplate(name, color),
            currents: createTimeseriesTemplate(name, color),
        }

        const now = Date.now()
        for (const feature of routes[i].route_geometry) {
            if (feature.geometry.type === 'Point') continue

            if (
                !feature.properties.spotWeathers ||
                !feature.properties.spotWeathers.length
            )
                continue

            for (const spot of <SpotWeather[]>feature.properties.spotWeathers) {
                //! Todo: until we get latest forecast version for all timestamps,
                //! historical weather data should be skipped. Remove this 'continue'
                //! when fixed
                if (new Date(spot.timestamp).getTime() < now) {
                    continue
                }
                if (spot.weather.Wind50M) {
                    weatherSeries.wind50M.timeseries.timestamps.push(
                        new Date(spot.timestamp).getTime(),
                    )
                    weatherSeries.wind50M.timeseries.direction!.push(
                        spot.weather.Wind50M.direction,
                    )
                    weatherSeries.wind50M.timeseries.values.push(
                        parseFloat(
                            mpsToKnots(spot.weather.Wind50M.value).toFixed(1),
                        ),
                    )
                }
                if (spot.weather.Swell) {
                    weatherSeries.swell.timeseries.timestamps.push(
                        new Date(spot.timestamp).getTime(),
                    )
                    weatherSeries.swell.timeseries.direction!.push(
                        spot.weather.Swell.direction,
                    )
                    weatherSeries.swell.timeseries.values.push(
                        parseFloat(spot.weather.Swell.value.toFixed(1)),
                    )
                }
                if (spot.weather.Wave) {
                    weatherSeries.wave.timeseries.timestamps.push(
                        new Date(spot.timestamp).getTime(),
                    )
                    weatherSeries.wave.timeseries.direction!.push(
                        spot.weather.Wave.direction,
                    )
                    weatherSeries.wave.timeseries.values.push(
                        parseFloat(spot.weather.Wave.value.toFixed(1)),
                    )
                }
                if (spot.weather.Currents) {
                    let direction = spot.weather.Currents.direction
                    if (direction) {
                        if (direction > 180) {
                            direction -= 180
                        } else {
                            direction += 180
                        }
                    }

                    weatherSeries.currents.timeseries.timestamps.push(
                        new Date(spot.timestamp).getTime(),
                    )
                    weatherSeries.currents.timeseries.direction!.push(direction)
                    weatherSeries.currents.timeseries.values.push(
                        parseFloat(
                            mpsToKnots(spot.weather.Currents.value).toFixed(1),
                        ),
                    )
                }
            }
        }

        updatedWeatherData.wind50M.push(weatherSeries.wind50M)
        updatedWeatherData.swell.push(weatherSeries.swell)
        updatedWeatherData.wave.push(weatherSeries.wave)
        updatedWeatherData.currents.push(weatherSeries.currents)
    }

    return updatedWeatherData
}

export function fixupInvalidPosValues(p: VesselPositionData) {
    if (p.sog && p.sog >= 102.3) {
        p.sog = null
    }

    if (p.heading && p.heading >= 511) {
        p.heading = null
    }

    if (p.course && p.course >= 511) {
        p.course = null
    }
}

function toDMS(degree: any, isLat = true) {
    const val = toDMM(degree, isLat)
    // const absolute = Math.abs(degree);
    // const degrees = Math.floor(absolute);
    // const minutesFloat = (absolute - degrees) * 60;
    // const minutes = Math.floor(minutesFloat);
    // const seconds = Math.round((minutesFloat - minutes) * 60);

    // const direction = degree >= 0
    //   ? (isLat ? 'N' : 'E')
    //   : (isLat ? 'S' : 'W');

    return `${val.degrees}°${val.minutes}′${val.seconds}″${val.hemisphere}`
}
function toDMM(decimal: any, isLat = true) {
    const absolute = Math.abs(decimal)
    const degrees = Math.floor(absolute)
    const minutesFloat = (absolute - degrees) * 60
    const minutes = Math.round(minutesFloat * 100) / 100
    const secondsFloat = (minutesFloat - minutes) * 60
    const seconds = Math.round(secondsFloat)

    const hemisphere = decimal >= 0 ? (isLat ? 'N' : 'E') : isLat ? 'S' : 'W'

    return {
        degrees,
        minutes,
        seconds,
        hemisphere,
    }
}

export function formatDate(dateString: string) {
    const date = new Date(dateString);

    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${day}/${month} ${hours}:${minutes} UTC.`;
  }

 export function getWrPopupContentForOriginalPlan(
    wrAdvice: UnReadWRAdvice
 ) {
    const sharedStyles = "padding-left: 16px; padding-right: 16px; font-family: 'MaerskText';";
    const paragraphStyles = "margin-bottom: 12px; font-size: 14px; font-weight: 400; font-family: 'MaerskText';";

    return html`
        <div style="${sharedStyles}">
            <p style="${paragraphStyles}">
              Your suggested route has been reviewed and accepted by WNI.
            </p>
            <p style="${paragraphStyles}">
                You can view the pdf of the original plan by clicking on View Pdf.
            </p>
        </div>
    `;
 }


  export function getWrPopupContent(
    wrAdvice: UnReadWRAdvice, 
    routeToBeReplaced?: string, 
    isWeatherRouteUpdate?: boolean
) {
    const adviceType = isWeatherRouteUpdate ? 'update' : 'advice';
    const message = generateCombinedSafetyAndPerformanceMessage(wrAdvice.safety_checks);
    const sharedStyles = "padding-left: 16px; padding-right: 16px; font-family: 'MaerskText';";
    const paragraphStyles = "margin-bottom: 12px; font-size: 14px; font-weight: 400; font-family: 'MaerskText';";
    const listStyles = "padding-left: 20px; font-size: 14px; font-weight: 400; font-family: 'MaerskText';";

    const safetyCheckPassedElement = html`
        <div style="${sharedStyles}">
            <p style="${paragraphStyles}">
                A new weather routing ${adviceType} has been received, which has passed the <br />
                Maersk Safety and Fuel Efficiency limits. View it on the planning window.
            </p>
            ${
                routeToBeReplaced
                    ? html`
                        <p style="margin-top: 12px; line-height: 1.5; font-family: 'MaerskText';">
                            By clicking on view ${adviceType}, <strong>${routeToBeReplaced}</strong> will be replaced by the Weather Routing plan.
                        </p>
                    `
                    : ''
            }
        </div>
    `;

    const safetyCheckFailedElement = html`
        <div style="${sharedStyles}">
            <p style="${paragraphStyles}">
                A new weather routing ${adviceType} has been received, found to be breaching Maersk Safety and/or <br />
                Performance limits. ${message[0]}
            </p>
            <ul style="${listStyles}">
                ${
                    // Exclude the first message, which is the prefix
                    message.slice(1).map((msg) => html`<li>${msg}</li>`)
                }
            </ul>
            <p style="${paragraphStyles}">
                View the weather routing ${adviceType} on the planning window.
            </p>
            ${
                routeToBeReplaced
                    ? html`
                        <p style="margin-top: 12px; line-height: 1.5; font-family: 'MaerskText';">
                            By clicking on view ${adviceType}, <strong>${routeToBeReplaced}</strong> will be replaced by the Weather Routing plan.
                        </p>
                    `
                    : ''
            }
        </div>
    `;

    return wrAdvice?.safety_checks?.isPassed 
        ? safetyCheckPassedElement 
        : safetyCheckFailedElement;
}


function formatToSingleDecimal(value: string): string {
    return parseFloat(value).toFixed(1);
}

const MeasurementUnits :  { [key: string]: string } = {
    "Maximum Wind Speed": "m/s",
    "Wave Height": "m"
  };


function generateCombinedSafetyAndPerformanceMessage(safetyChecks?: SafetyChecks): string[] {
    if(!safetyChecks) return [];
    const messages: string[] = [];
    let failingSafetyChecks = false;
    let failingPerformanceChecks = false;

    // Combine heavy weather violations
    if(safetyChecks?.heavyWeather && safetyChecks?.heavyWeather?.length > 0) {
        const heavyWeatherViolations = safetyChecks?.heavyWeather
            .filter((condition) => !condition.isPassed)
            .map(
                (condition) =>
                    `${condition.key}: Expected to reach ${formatToSingleDecimal(
                        condition.value 
                    )} ${MeasurementUnits[condition.key] ?? ''}, exceeding safety limits (${formatToSingleDecimal(
                        condition.limit 
                    )} ${MeasurementUnits[condition.key] ?? ''}).`
            );

        if (heavyWeatherViolations?.length > 0) {
            messages.push(...heavyWeatherViolations);
            failingSafetyChecks = true;
        }
    }

    // Parametric roll details
    const parametricRoll = safetyChecks?.parametricRoll;
    if (parametricRoll && !parametricRoll.isPassed) {
        messages.push(
            `Parametric Roll: Predicted for ${parseInt(
                parametricRoll.duration
            )} hours in the next ${parseInt(
                parametricRoll.occurrence
            )} hours.`
        );
        failingSafetyChecks = true;
    }

    // Performance limits
    const performance = safetyChecks?.performance;
    if (performance && !performance?.isPassed) {
        const voyageLength = parseFloat(performance.voyage_length_value);
        const voyageLengthLimit = parseFloat(performance.voyage_length_limit);
        const fuelValue = parseFloat(performance.voyage_fuel_value);
        const fuelMinLimit = parseFloat(performance.voyage_fuel_min_limit);
        const fuelMaxLimit = parseFloat(performance.voyage_fuel_max_limit);

        if (voyageLength < voyageLengthLimit && fuelValue > fuelMinLimit) {
            messages.push(
                `Fuel Consumption: Estimated to exceed ${formatToSingleDecimal(
                    performance.voyage_fuel_value
                )} MT for routes higher than the vessel's planned route.`
            );
        } else if (voyageLength >= voyageLengthLimit && fuelValue > fuelMaxLimit) {
            messages.push(
                `Fuel Consumption: Estimated to exceed ${formatToSingleDecimal(
                    performance.voyage_fuel_max_limit
                )} MT for routes longer than ${formatToSingleDecimal(
                    performance.voyage_length_limit
                )} NM.`
            );
        }
        failingPerformanceChecks = true;
    }

    // ETA issues
    const eta = safetyChecks?.eta;
    if (eta && !eta?.isPassed) {
        messages.push(
            `ETA Delay: Weather routing advice may result in a delay of ${parseInt(eta.value)} hrs.`
        );
        failingSafetyChecks = true;
    }

    // Add final message prefix if both safety and performance checks fail
    if (failingSafetyChecks || failingPerformanceChecks) {
        const messagePrefix =
            failingSafetyChecks && failingPerformanceChecks
                ? "Safety and Performance"
                : failingSafetyChecks
                ? "Safety"
                : "Performance";
        messages.unshift(
            `The following conditions exceed Maersk's ${messagePrefix} thresholds:`
        );
    }

    return messages;
}

// Example usage
const safetyChecks: SafetyChecks = {
    isPassed: false,
    eta: {
        isPassed: false,
        value: "16.0h"
    },
    performance: {
        isPassed: true,
        voyage_length_limit: "2500.0",
        voyage_fuel_min_limit: "30.0",
        voyage_fuel_max_limit: "70.0",
        voyage_length_value: "2800.0",
        voyage_fuel_value: "80.0"
    },
    parametricRoll: {
        isPassed: true,
        duration: "8.707778",
        occurrence: "60.685771"
    },
    heavyWeather: [
        {
            key: "Maximum Wind Speed",
            isPassed: false,
            limit: "50.0",
            value: "17.0",
            unit: 'm/s'
        },
        {
            key: "Wave Height",
            isPassed: true,
            limit: "5.0",
            value: "6.0",
            unit: 'm'
        }
    ]
};
